import React, { useMemo, useState } from 'react';

import { Modal } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faFileAlt } from '@fortawesome/pro-solid-svg-icons';

import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import SearchInput from 'components/search_sidebar/sidebar_search';

export interface SaveScopeDocument {
  id: number;
  name: string;
}

export interface SaveScope {
  allDocuments: boolean;
  documentIds: number[];
}

interface Props {
  documents?: SaveScopeDocument[];
  // Opens on "Choose documents" with only these checked, when any of them are in documents
  initialDocumentIds?: number[];
  pageName?: string;
  saving: boolean;
  show: boolean;
  onClose: () => void;
  onSave: (scope: SaveScope) => void;
}

export default function SavePageModal({
  documents,
  initialDocumentIds = [],
  pageName,
  saving,
  show,
  onClose,
  onSave,
}: Props) {
  const chooserMode = !!documents;
  const documentIds = (documents || []).map(doc => doc.id);
  const preselectedIds = documentIds.filter(id => initialDocumentIds.includes(id));
  const isPreselected = preselectedIds.length > 0;
  // The document editor keeps its existing "this document" default; the library defaults to All Documents.
  const [allDocuments, setAllDocuments] = useState(chooserMode && !isPreselected);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>(isPreselected ? preselectedIds : documentIds);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = documents || [];

    return term ? all.filter(doc => doc.name.toLowerCase().includes(term)) : all;
  }, [documents, search]);

  const toggle = (id: number, checked: boolean) => {
    setSelectedIds(current => (checked ? [...current, id] : current.filter(i => i !== id)));
  };

  const selectAllVisible = () => {
    setSelectedIds(current => [...new Set([...current, ...visible.map(doc => doc.id)])]);
  };

  const nothingChosen = chooserMode && !allDocuments && selectedIds.length === 0;
  const close = () => !saving && onClose();

  return (
    <Modal show={show} onHide={close}>
      <Modal.Header closeButton>
        Page Default Changes
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          You can make these changes to all your documents OR
          {chooserMode ? ' only the ones you choose.' : ' this document only.'}
        </div>
        <strong className="fw-semibold">
          Where would you like to save these changes?
        </strong>
        <div
          className="row"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAllDocuments(e.target.value === 'true')}
        >
          <label className="radio radio-inline col-5 border px-5 py-4 m-4">
            <input checked={allDocuments} name="save-scope" readOnly type="radio" value="true" />
            <FontAwesomeIcon className="mx-2" icon={faFile} size="lg" />
            All Documents
          </label>
          <label className="radio radio-inline col-5 border px-5 py-4 m-4">
            <input checked={!allDocuments} name="save-scope" readOnly type="radio" value="false" />
            <FontAwesomeIcon className="mx-2" icon={faFileAlt} size="lg" />
            {chooserMode ? 'Choose documents' : pageName}
          </label>
        </div>

        {chooserMode && !allDocuments && (
          <div className="mt-3">
            <p className="text-muted">
              This page is used in {documents.length} {documents.length === 1 ? 'document' : 'documents'}.
              The ones you leave unchecked keep the page as it is today.
            </p>
            <div className="d-flex align-items-center gap-3 mb-2">
              <SearchInput
                placeholder="Search documents"
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                onClear={() => setSearch('')}
              />
              <Button size="sm" variant="ghost" onClick={selectAllVisible}>Select all</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>Clear</Button>
            </div>
            {visible.map(doc => (
              <div className="py-1" key={doc.id}>
                <Checkbox
                  checked={selectedIds.includes(doc.id)}
                  label={doc.name}
                  onCheckedChange={(checked: boolean | 'indeterminate') => toggle(doc.id, checked === true)}
                />
              </div>
            ))}
            {visible.length === 0 && <p className="text-muted mb-0">No documents match.</p>}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={saving} label="Cancel" variant="ghost" onClick={close} />
        <Button
          disabled={saving || nothingChosen}
          label={saving ? 'Saving ...' : 'Save Changes'}
          onClick={() => onSave({ allDocuments, documentIds: allDocuments ? [] : selectedIds })}
        />
      </Modal.Footer>
    </Modal>
  );
}
